import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form-field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { usePayments } from '@/hooks/use-payments';
import { useAuth } from '@/hooks/use-auth';
import { toast } from 'sonner';

interface AddPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddPaymentDialog({ open, onOpenChange }: AddPaymentDialogProps) {
  const { addPayment } = usePayments();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    memberName: '',
    amount: '',
    dueDate: '',
    description: 'Monthly membership fee',
    status: 'pending' as 'paid' | 'pending' | 'overdue',
    paymentDate: '',
    paymentMethod: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.memberName || !formData.amount || !formData.dueDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await addPayment({
        memberId: `member-${Date.now()}`,
        memberName: formData.memberName,
        amount: parseFloat(formData.amount),
        currency: 'USD',
        paymentDate: formData.paymentDate,
        dueDate: formData.dueDate,
        status: formData.status,
        paymentMethod: formData.paymentMethod,
        description: formData.description,
        adminId: user?.id,
      });
      toast.success('Payment record added successfully');
      onOpenChange(false);
      setFormData({
        memberName: '',
        amount: '',
        dueDate: '',
        description: 'Monthly membership fee',
        status: 'pending',
        paymentDate: '',
        paymentMethod: '',
      });
    } catch (err) {
      console.error(err);
      toast.error('Failed to add payment');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add Payment Record</DialogTitle>
          <DialogDescription>
            Create a new payment record for a member.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormField
            label="Member Name"
            value={formData.memberName}
            onChange={(e) => setFormData({ ...formData, memberName: e.target.value })}
            required
          />
          
          <FormField
            label="Amount"
            type="number"
            step="0.01"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            required
          />
          
          <FormField
            label="Due Date"
            type="date"
            value={formData.dueDate}
            onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
            required
          />
          
          <FormField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Status</label>
            <Select 
              value={formData.status} 
              onValueChange={(value: 'paid' | 'pending' | 'overdue') => 
                setFormData({ ...formData, status: value })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {formData.status === 'paid' && (
            <>
              <FormField
                label="Payment Date"
                type="date"
                value={formData.paymentDate}
                onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
                required
              />
              
              <FormField
                label="Payment Method"
                value={formData.paymentMethod}
                onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                placeholder="e.g., Credit Card, Cash, Bank Transfer"
              />
            </>
          )}
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">Add Payment</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}